
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { BasicSearchFields } from './sections/BasicSearchFields';
import { AdvancedOptionsAccordion } from './sections/AdvancedOptionsAccordion';
import { SearchProviderSelector } from './sections/SearchProviderSelector';
import { useJobSearchForm } from '@/hooks/useJobSearchForm';
import { JobSearchCriteria } from '@/types/jobSearch';
import { Search } from 'lucide-react';

interface JobSearchFormProps {
  onSubmit: (criteria: JobSearchCriteria, providerId: string) => void;
  isLoading: boolean;
  initialCriteria?: JobSearchCriteria;
  onCriteriaChange?: (criteria: JobSearchCriteria) => void;
}

const availableProviders = [
  { id: 'lutra-webhook', name: 'Lutra AI', description: 'AI-powered job search' }
];

export const JobSearchForm: React.FC<JobSearchFormProps> = ({ 
  onSubmit, 
  isLoading,
  initialCriteria,
  onCriteriaChange
}) => {
  const { form, handleSubmit } = useJobSearchForm(onSubmit, initialCriteria);

  // Watch for form changes and notify parent
  React.useEffect(() => {
    if (onCriteriaChange) {
      const subscription = form.watch((value) => {
        if (value) {
          onCriteriaChange({
            industry: value.industry || '',
            jobRole: value.jobRole || '',
            geography: value.geography || '',
            keywords: value.keywords,
            maxPostedWeeks: value.maxPostedWeeks,
          });
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [form, onCriteriaChange]);

  // Only show provider selector if there are multiple providers
  const shouldShowProviderSelector = availableProviders.length > 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Search className="h-5 w-5" />
          <span>Job Search</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <BasicSearchFields 
              control={form.control} 
              errors={form.formState.errors} 
              isLoading={isLoading} 
            />
            <AdvancedOptionsAccordion 
              control={form.control} 
              errors={form.formState.errors} 
              isLoading={isLoading} 
            />
            {shouldShowProviderSelector && (
              <SearchProviderSelector 
                selectedProvider={form.watch('providerId') || 'lutra-webhook'}
                onValueChange={(value) => form.setValue('providerId', value)}
                availableProviders={availableProviders}
                isLoading={isLoading}
              />
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search for Opportunities
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
